// Supabase Edge Functions API base
// All API calls now go through Supabase Edge Functions instead of Railway

import { supabase } from './supabase';

function trimTrailingSlash(value?: string): string {
  return value?.trim().replace(/\/+$/, '') || '';
}

function normalizeEnvValue(value?: string): string {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  return trimmed.includes('=') ? trimmed.split('=').slice(1).join('=').trim() : trimmed;
}

const resolvedSupabaseUrl = normalizeEnvValue(
  import.meta.env.VITE_SUPABASE_URL || import.meta.env.SUPABASE_URL || ''
);

function buildFunctionsBase(supabaseUrl?: string): string {
  const normalizedUrl = normalizeEnvValue(supabaseUrl);
  return normalizedUrl ? `${trimTrailingSlash(normalizedUrl)}/functions/v1` : '';
}

function getRuntimeSupabaseUrl(): string {
  if (!supabase) {
    return '';
  }

  const client = supabase as unknown as { supabaseUrl?: string };
  return normalizeEnvValue(client.supabaseUrl);
}

export const SUPABASE_FUNCTIONS_BASE = buildFunctionsBase(resolvedSupabaseUrl);

const LEGACY_ORCHESTRATION_BASE = trimTrailingSlash(
  normalizeEnvValue(import.meta.env.VITE_ORCHESTRATION_API_URL)
);
const supabaseAnonKey = normalizeEnvValue(
  import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.SUPABASE_ANON_KEY || ''
);

export function getOrchestrationApiBase(): string {
  return SUPABASE_FUNCTIONS_BASE || buildFunctionsBase(getRuntimeSupabaseUrl()) || LEGACY_ORCHESTRATION_BASE;
}

// Backwards compatibility - orchestrationApiBase now points to Supabase Functions
export const orchestrationOrigin = getOrchestrationApiBase();
export const orchestrationApiBase = orchestrationOrigin;

let authHeadersCache:
  | {
      headers: Record<string, string>;
      expiresAt: number;
    }
  | null = null;
let authHeadersPromise: Promise<Record<string, string>> | null = null;
let latestAccessToken: string | null = null;

export function clearAuthHeadersCache() {
  authHeadersCache = null;
  authHeadersPromise = null;
}

export function primeAuthHeaders(accessToken?: string | null) {
  latestAccessToken = accessToken?.trim() || null;
  authHeadersCache = null;
  authHeadersPromise = null;
}

// Helper to build Edge Function invocation URL
// For direct fetch calls (not using supabase.functions.invoke)
export function withOrchestrationApi(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const apiBase = getOrchestrationApiBase();
  // Map old API paths to Edge Functions
  const functionMap: Record<string, string> = {
    '/api/orders': '/orders',
    '/api/orders/': '/orders/',
    '/api/payments/daraja/stkpush': '/payments-stk',
    '/api/payments/daraja/callback': '/payments-callback',
    '/api/pricing': '/pricing-all',
    '/api/pricing/': '/pricing-',
    '/api/admin/branches': '/admin-branches',
    '/api/admin/riders': '/admin-riders',
    '/api/admin/admins': '/admin-users',
    '/api/admin/dashboard': '/admin-dashboard',
    '/api/admin/audit-log': '/admin-audit',
    '/api/admin/webhook-events': '/admin-webhooks',
    '/api/admin/setup': '/admin-setup',
    '/api/riders/login': '/rider-login',
    '/api/whatsapp/parse': '/whatsapp-parse',
    '/api/whatsapp/create-order': '/whatsapp-create-order',
    '/api/webhook/mautrix': '/webhook-mautrix',
    '/api/webhook/health': '/webhook-health',
  };

  // Check for exact match first
  if (functionMap[normalizedPath]) {
    return apiBase ? `${apiBase}${functionMap[normalizedPath]}` : functionMap[normalizedPath];
  }

  // Check for prefix matches
  for (const [prefix, fnPrefix] of Object.entries(functionMap)) {
    if (normalizedPath.startsWith(prefix)) {
      const rest = normalizedPath.slice(prefix.length);
      return apiBase ? `${apiBase}${fnPrefix}${rest}` : `${fnPrefix}${rest}`;
    }
  }

  return apiBase ? `${apiBase}${normalizedPath}` : normalizedPath;
}

// Invoke a Supabase Edge Function with auth token
export async function invokeFunction(name: string, body: Record<string, unknown>, options?: { method?: string }) {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  const { data, error } = await supabase.functions.invoke(name, {
    body,
    ...options,
  });

  if (error) {
    throw error;
  }

  return data;
}

// Get auth headers for Edge Function calls
export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!supabase) return {};

  if (authHeadersCache && authHeadersCache.expiresAt > Date.now()) {
    return authHeadersCache.headers;
  }

  if (authHeadersPromise) {
    return authHeadersPromise;
  }

  authHeadersPromise = supabase.auth
    .getSession()
    .then(({ data }) => {
      const accessToken = data.session?.access_token || latestAccessToken;
      const headers: Record<string, string> = {};

      if (supabaseAnonKey) {
        headers.apikey = supabaseAnonKey;
      }

      if (accessToken) {
        latestAccessToken = accessToken;
        headers.Authorization = `Bearer ${accessToken}`;
      } else if (supabaseAnonKey) {
        // Supabase Edge Functions expect an Authorization header even for anon calls.
        headers.Authorization = `Bearer ${supabaseAnonKey}`;
      }

      authHeadersCache = {
        headers,
        expiresAt: Date.now() + 10_000,
      };

      return headers;
    })
    .finally(() => {
      authHeadersPromise = null;
    });

  return authHeadersPromise;
}

// Re-export supabase for direct use
export { supabase };
